import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Mail, ChevronRight, Bell, Key, Globe } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import EmailPreferencesModal from './EmailPreferencesModal';
import VendorContractRenewalSettingsModal from './VendorContractRenewalSettingsModal';
import LanguagePreferencesModal from './LanguagePreferencesModal';
import ApiKeyManagement from './ApiKeyManagement';

interface EmailPreferences {
  taskReportEnabled: boolean;
  taskReportFrequency: 'daily' | 'weekly' | 'monthly';
  taskReportDay: number;
  taskReportTime: string;
}

interface VendorContractRenewalPreferences {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  day: number;
  time: string;
  daysBeforeExpiry: number[]; // Array of days before expiry
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [emailPrefs, setEmailPrefs] = useState<EmailPreferences | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [renewalPrefs, setRenewalPrefs] = useState<VendorContractRenewalPreferences | null>(null);
  const [isRenewalModalOpen, setIsRenewalModalOpen] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState<string>('en');
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [showApiKeys, setShowApiKeys] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
      loadRenewalPreferences();
      loadLanguagePreferences();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  const loadPreferences = async () => {
    if (!user?.id) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.id));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const prefs = data.emailPreferences as EmailPreferences | undefined;
        setEmailPrefs(prefs || null);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRenewalPreferences = async () => {
    if (!user?.id) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.id));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const prefs = data.vendorContractRenewalNotifications as VendorContractRenewalPreferences | undefined;
        setRenewalPrefs(prefs || null);
      }
    } catch (error) {
      console.error('Error loading renewal preferences:', error);
    }
  };

  const loadLanguagePreferences = async () => {
    if (!user?.id) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.id));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const lang = data.preferredLanguage as string | undefined;
        setPreferredLanguage(lang === 'es' ? 'es' : 'en');
      }
    } catch (error) {
      console.error('Error loading language preferences:', error);
    }
  };

  const getRenewalSummary = (prefs: VendorContractRenewalPreferences | null): string => {
    // Default values for backward compatibility: weekly on Monday at 8 AM MST, 30 days before expiry
    const defaultPrefs: VendorContractRenewalPreferences = {
      enabled: true,
      frequency: 'weekly',
      day: 1, // Monday
      time: '08:00', // 8 AM MST
      daysBeforeExpiry: [30],
    };

    // Use default if no preferences set (backward compatibility)
    const effectivePrefs = prefs || defaultPrefs;

    if (effectivePrefs.enabled === false) {
      return 'Disabled';
    }

    const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    let frequencyText = '';
    if (effectivePrefs.frequency === 'daily') {
      frequencyText = 'Every day';
    } else if (effectivePrefs.frequency === 'weekly') {
      frequencyText = `Every ${weekDays[effectivePrefs.day]}`;
    } else if (effectivePrefs.frequency === 'monthly') {
      const day = effectivePrefs.day;
      const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                     day === 2 || day === 22 ? 'nd' : 
                     day === 3 || day === 23 ? 'rd' : 'th';
      frequencyText = `${day}${suffix} of each month`;
    }

    // Handle backward compatibility: if daysBeforeExpiry is a number, convert to array
    let daysArray = effectivePrefs.daysBeforeExpiry;
    if (typeof daysArray === 'number') {
      daysArray = [daysArray];
    } else if (!Array.isArray(daysArray) || daysArray.length === 0) {
      daysArray = [30];
    }

    const daysText = daysArray.length === 1 
      ? `${daysArray[0]} day${daysArray[0] === 1 ? '' : 's'}`
      : `${daysArray.join(', ')} days`;

    return `${frequencyText} at ${effectivePrefs.time} MST (${daysText} before expiry)`;
  };

  const getEmailSummary = (prefs: EmailPreferences | null): string => {
    // Default values for backward compatibility: weekly on Monday at 9 AM UTC
    const defaultPrefs: EmailPreferences = {
      taskReportEnabled: true,
      taskReportFrequency: 'weekly',
      taskReportDay: 1, // Monday
      taskReportTime: '09:00',
    };

    // Use default if no preferences set (backward compatibility)
    const effectivePrefs = prefs || defaultPrefs;

    if (effectivePrefs.taskReportEnabled === false) {
      return 'Disabled';
    }

    const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    let frequencyText = '';
    if (effectivePrefs.taskReportFrequency === 'daily') {
      frequencyText = 'Every day';
    } else if (effectivePrefs.taskReportFrequency === 'weekly') {
      frequencyText = `Every ${weekDays[effectivePrefs.taskReportDay]}`;
    } else if (effectivePrefs.taskReportFrequency === 'monthly') {
      const day = effectivePrefs.taskReportDay;
      const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                     day === 2 || day === 22 ? 'nd' : 
                     day === 3 || day === 23 ? 'rd' : 'th';
      frequencyText = `${day}${suffix} of each month`;
    }

    return `${frequencyText} at ${effectivePrefs.taskReportTime} UTC`;
  };

  const getLanguageSummary = (lang: string): string => {
    if (lang === 'es') {
      return 'Español (Spanish)';
    }
    return 'English';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-600">Manage your application preferences</p>
      </div>

      {/* Settings Sections */}
      <div className="space-y-2">
        {/* Email Preferences Section */}
        <div 
          className="card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setIsEmailModalOpen(true)}
        >
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Compliance Task Reports</h3>
                <p className="text-xs text-gray-500">
                  {getEmailSummary(emailPrefs)}
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Vendor Contract Renewal Notifications Section */}
        <div 
          className="card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setIsRenewalModalOpen(true)}
        >
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Bell className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Vendor Contract Renewal Notifications</h3>
                <p className="text-xs text-gray-500">
                  {getRenewalSummary(renewalPrefs)}
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Language Preferences Section */}
        <div 
          className="card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setIsLanguageModalOpen(true)}
        >
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Globe className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Language</h3>
                <p className="text-xs text-gray-500">
                  {getLanguageSummary(preferredLanguage)}
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* API Keys Section */}
        <div 
          className="card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setShowApiKeys(true)}
        >
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Key className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">API Keys</h3>
                <p className="text-xs text-gray-500">
                  Manage API keys for ChatGPT and other integrations
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Email Preferences Modal */}
      <EmailPreferencesModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          loadPreferences(); // Reload preferences after closing
        }}
      />

      {/* Vendor Contract Renewal Settings Modal */}
      <VendorContractRenewalSettingsModal
        isOpen={isRenewalModalOpen}
        onClose={() => {
          setIsRenewalModalOpen(false);
          loadRenewalPreferences(); // Reload preferences after closing
        }}
      />

      {/* Language Preferences Modal */}
      <LanguagePreferencesModal
        isOpen={isLanguageModalOpen}
        onClose={() => {
          setIsLanguageModalOpen(false);
          loadLanguagePreferences(); // Reload preferences after closing
        }}
      />

      {/* API Keys Management Modal */}
      {showApiKeys && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">API Key Management</h2>
              <button
                onClick={() => setShowApiKeys(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ChevronRight className="w-6 h-6 rotate-90" />
              </button>
            </div>
            <div className="p-6">
              <ApiKeyManagement />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
